import {
    doc,
    getDoc,
    setDoc,
    onSnapshot,
    collection,
    getDocs,
    serverTimestamp,
    type DocumentSnapshot,
    type Firestore
} from 'firebase/firestore';
import { throttle } from '@/lib/utils/utils';

/**
 * Service for managing manuscript annotations in Firestore.
 * Path: users/{userId}/manuscripts/{manuscriptId}/passages/{passageId}
 */

export const ManuscriptService = {
    /**
     * Fetch all marks for a specific passage in a manuscript.
     */
    async fetchPassageMarks(
        db: Firestore,
        userId: string,
        manuscriptId: string,
        passageId: string
    ): Promise<ManuscriptPassageMarks | null> {
        if (!userId || !manuscriptId || !passageId) return null;

        const docRef = doc(db, 'users', userId, 'manuscripts', manuscriptId, 'passages', passageId);
        const snapshot = await getDoc(docRef);

        if (snapshot.exists()) {
            return snapshot.data() as ManuscriptPassageMarks;
        }
        return null;
    },

    /**
     * Subscribe to real-time updates for a passage's marks.
     */
    subscribeToPassageMarks(
        db: Firestore,
        userId: string,
        manuscriptId: string,
        passageId: string,
        onUpdate: (data: ManuscriptPassageMarks) => void
    ) {
        if (!userId || !manuscriptId || !passageId) return () => { };

        const docRef = doc(db, 'users', userId, 'manuscripts', manuscriptId, 'passages', passageId);

        return onSnapshot(docRef, (snapshot: DocumentSnapshot) => {
            if (snapshot.exists()) {
                onUpdate(snapshot.data() as ManuscriptPassageMarks);
            } else {
                // Initialize empty document if it doesn't exist
                onUpdate({
                    passageId,
                    lastUpdated: Date.now(),
                    lastUsedAt: Date.now(),
                    marks: [],
                    connections: [],
                    dividers: []
                });
            }
        });
    },

    /**
     * Save marks to Firestore.
     */
    savePassageMarks: throttle(async (
        db: Firestore,
        userId: string,
        manuscriptId: string,
        passageId: string,
        data: Partial<ManuscriptPassageMarks>
    ) => {
        if (!userId || !manuscriptId || !passageId) return;

        const docRef = doc(db, 'users', userId, 'manuscripts', manuscriptId, 'passages', passageId);

        try {
            await setDoc(docRef, {
                ...data,
                passageId,
                lastUpdated: Date.now(),
                lastUsedAt: Date.now(),
                serverTimestamp: serverTimestamp()
            }, { merge: true });
        } catch (error) {
            console.error('[ManuscriptService] Error saving marks:', error);
            throw error;
        }
    }, 1000),

    /**
     * Aggregates mark style usage counts across all passages in a manuscript.
     */
    async getMarkStyleUsageCounts(
        db: Firestore,
        userId: string,
        manuscriptId: string
    ): Promise<Record<string, number>> {
        if (!userId || !manuscriptId) return {};

        const colRef = collection(db, 'users', userId, 'manuscripts', manuscriptId, 'passages');
        const snapshot = await getDocs(colRef);

        const counts: Record<string, number> = {};

        snapshot.docs.forEach(doc => {
            const data = doc.data() as ManuscriptPassageMarks;
            data.marks?.forEach(mark => {
                if (mark.markStyleId) {
                    counts[mark.markStyleId] = (counts[mark.markStyleId] || 0) + 1;
                }
            });
        });

        return counts;
    }
};
